/**
 * What-If Backtest Modal — visual surface for the hypothetical replay.
 *
 * Renders the actual vs hypothetical equity curves side by side along with
 * the headline delta (would-have-been vs actual P&L), per-setup attribution,
 * and a rule-set selector so the trader can flip between
 * "recommended confluences", "no counter-confluences", and "all rules."
 *
 * Uses recharts (already a project dep for the equity chart in StatsCards
 * and elsewhere) so this stays consistent with the rest of the app's
 * chart styling.
 *
 * Props:
 *   trades            All closed trades (host filters to "what counts")
 *   setupDetails      Setup metadata (recommendedConfluences source)
 *   getTradeNetPnL    Commission-aware netPnl callback
 *   getAccountById
 *   onClose           () => void
 *   darkMode          bool
 */

import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X, FlaskConical, TrendingUp, TrendingDown, ArrowRight, Info, Clock,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from 'recharts';
import { runBacktest, RULES } from '../../services/coach/whatIf/runBacktest';

// ── Formatting ───────────────────────────────────────────────────────

const fmtMoney = (n, opts = {}) => {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(Math.round(n));
  const sign = n >= 0 ? (opts.alwaysSign ? '+' : '') : '−';
  return `${sign}$${abs.toLocaleString()}`;
};
const fmtPct = (n) => Number.isFinite(n) ? `${Math.round(n * 100)}%` : '—';
const fmtDeltaPct = (n) => {
  if (!Number.isFinite(n)) return '—';
  const sign = n >= 0 ? '+' : '−';
  return `${sign}${Math.abs(Math.round(n * 100))}pp`;
};

// ── Sub-components ───────────────────────────────────────────────────

const StatTile = ({ label, value, sub, accent = 'sky', darkMode }) => {
  const cls = {
    emerald: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/20',
    red:     'text-red-300 bg-red-500/10 border-red-500/20',
    sky:     'text-sky-300 bg-sky-500/10 border-sky-500/20',
    purple:  'text-purple-300 bg-purple-500/10 border-purple-500/20',
    amber:   'text-amber-300 bg-amber-500/10 border-amber-500/20',
  }[accent];
  return (
    <div className={`rounded-lg border p-3 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80 mb-1">{label}</div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
      {sub && <div className={`text-[11px] mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{sub}</div>}
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────

const WhatIfBacktestModal = ({
  trades = [],
  setupDetails = {},
  getTradeNetPnL,
  getAccountById,
  onClose,
  darkMode = true,
}) => {
  // Esc to close
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const [ruleSet, setRuleSet] = useState(RULES.RECOMMENDED_CONFLUENCES);

  // Recompute backtest when ruleSet changes. Memoized on trades + setupDetails
  // identity — host should keep these stable across reopens.
  const backtest = useMemo(
    () => runBacktest({ trades, setupDetails, getTradeNetPnL, getAccountById, ruleSet }),
    [trades, setupDetails, getTradeNetPnL, getAccountById, ruleSet]
  );

  // Combined equity-curve data: one row per date with both lines' values
  const chartData = useMemo(() => {
    if (!backtest) return [];
    const map = new Map();  // date → { date, actual, hypothetical }
    for (const pt of backtest.actual.equityCurve) {
      map.set(pt.date, { date: pt.date, actual: pt.cumulative, hypothetical: null });
    }
    for (const pt of backtest.hypothetical.equityCurve) {
      const existing = map.get(pt.date);
      if (existing) existing.hypothetical = pt.cumulative;
      else map.set(pt.date, { date: pt.date, actual: null, hypothetical: pt.cumulative });
    }
    // Forward-fill hypothetical: dates where the rule filtered out all trades
    // get no point of their own; we want the line to plateau at the prior
    // cumulative value rather than gap to null.
    const out = [...map.values()].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    let lastHypo = 0;
    for (const row of out) {
      if (row.hypothetical == null) row.hypothetical = lastHypo;
      else lastHypo = row.hypothetical;
    }
    return out;
  }, [backtest]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={`
          relative w-full max-w-5xl max-h-[90vh] flex flex-col
          rounded-xl shadow-2xl
          ${darkMode ? 'bg-gray-900 border border-white/10' : 'bg-white border border-gray-200'}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-start justify-between gap-3 p-5 border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="rounded-lg bg-amber-500/20 p-2 border border-amber-500/30 shrink-0">
              <FlaskConical size={20} className="text-amber-300" />
            </div>
            <div className="min-w-0">
              <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                What-If Backtest
              </h2>
              <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Hypothetical equity curve: what your P&L would look like if you'd only taken trades that meet the selected rule.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-1.5 rounded-lg shrink-0 ${darkMode ? 'hover:bg-white/5 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {!backtest && (
            <div className="text-center py-16">
              <div className="inline-flex p-4 rounded-full bg-amber-500/10 border border-amber-500/20 mb-4">
                <FlaskConical size={32} className="text-amber-300" />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Not enough data yet
              </h3>
              <p className={`text-sm max-w-md mx-auto ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                The backtest needs trades with a date + setup + P&L. Take a few trades and come back here.
              </p>
            </div>
          )}

          {backtest && (
            <>
              {/* Rule selector */}
              <div className={`mb-4 rounded-lg border p-3 ${darkMode ? 'bg-black/30 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                <div className={`text-xs uppercase tracking-wider mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Hypothetical rule
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { id: RULES.RECOMMENDED_CONFLUENCES, label: 'Only with all recommended confluences', sub: 'Setup-pinned must-haves were all present' },
                    { id: RULES.NO_COUNTER_CONFLUENCES,  label: 'Skip trades with counter-confluences', sub: 'Never overrode a documented warning' },
                    { id: RULES.ALL,                     label: 'Both rules (strict)', sub: 'Recommended present AND no counter overridden' },
                  ].map(r => {
                    const active = ruleSet === r.id;
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setRuleSet(r.id)}
                        className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                          active
                            ? (darkMode ? 'bg-purple-500/30 border-purple-400/50 text-purple-100' : 'bg-purple-100 border-purple-300 text-purple-800')
                            : (darkMode ? 'bg-gray-800/40 border-gray-700 text-gray-300 hover:bg-gray-800/70' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50')
                        }`}
                        title={r.sub}
                      >
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Headline delta */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <StatTile
                  label="Actual P&L"
                  value={fmtMoney(backtest.actual.netPnl, { alwaysSign: true })}
                  sub={`${backtest.actual.tradeCount} trades · ${fmtPct(backtest.actual.winRate)} WR`}
                  accent={backtest.actual.netPnl >= 0 ? 'emerald' : 'red'}
                  darkMode={darkMode}
                />
                <StatTile
                  label="Hypothetical P&L"
                  value={fmtMoney(backtest.hypothetical.netPnl, { alwaysSign: true })}
                  sub={`${backtest.hypothetical.tradeCount} trades · ${fmtPct(backtest.hypothetical.winRate)} WR`}
                  accent={backtest.hypothetical.netPnl >= 0 ? 'emerald' : 'red'}
                  darkMode={darkMode}
                />
                <StatTile
                  label="Δ vs actual"
                  value={fmtMoney(backtest.delta.netPnl, { alwaysSign: true })}
                  sub={`${fmtDeltaPct(backtest.delta.winRate)} WR`}
                  accent={backtest.delta.netPnl >= 0 ? 'emerald' : 'red'}
                  darkMode={darkMode}
                />
                <StatTile
                  label="Skipped"
                  value={backtest.hypothetical.skippedCount}
                  sub={`${fmtMoney(backtest.hypothetical.skippedNetPnl, { alwaysSign: true })} on those trades`}
                  accent="amber"
                  darkMode={darkMode}
                />
              </div>

              {/* Equity curve chart */}
              <div className={`mb-4 rounded-lg border p-3 ${darkMode ? 'bg-black/30 border-white/10' : 'bg-white border-gray-200'}`}>
                <div className={`text-xs uppercase tracking-wider mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Equity curve — actual vs hypothetical
                </div>
                <div style={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#1f2937' : '#e5e7eb'} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: darkMode ? '#9ca3af' : '#6b7280' }} />
                      <YAxis tick={{ fontSize: 10, fill: darkMode ? '#9ca3af' : '#6b7280' }} tickFormatter={(v) => fmtMoney(v)} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: darkMode ? '#1f2937' : '#fff',
                          border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                          borderRadius: 6, fontSize: 12,
                        }}
                        formatter={(value, name) => [fmtMoney(value, { alwaysSign: true }), name === 'actual' ? 'Actual' : 'Hypothetical']}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="actual"        stroke="#60a5fa" strokeWidth={2} dot={false} name="Actual" />
                      <Line type="monotone" dataKey="hypothetical"  stroke="#a78bfa" strokeWidth={2} dot={false} strokeDasharray="5 3" name="Hypothetical" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Per-setup attribution */}
              {backtest.perSetup.length > 0 && (
                <div className={`rounded-lg border p-3 ${darkMode ? 'bg-black/30 border-white/10' : 'bg-white border-gray-200'}`}>
                  <div className={`text-xs uppercase tracking-wider mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Per-setup contribution to the delta
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                          <th className="text-left py-1 px-2 font-medium">Setup</th>
                          <th className="text-right py-1 px-2 font-medium">Actual</th>
                          <th className="text-right py-1 px-2 font-medium">Hypothetical</th>
                          <th className="text-right py-1 px-2 font-medium">Δ</th>
                          <th className="text-right py-1 px-2 font-medium">Skipped</th>
                        </tr>
                      </thead>
                      <tbody>
                        {backtest.perSetup.map(s => {
                          const delta = s.hypotheticalNet - s.actualNet;
                          return (
                            <tr key={s.setup} className={darkMode ? 'border-t border-white/5' : 'border-t border-gray-200'}>
                              <td className={`py-1.5 px-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{s.setup}</td>
                              <td className="py-1.5 px-2 text-right font-mono tabular-nums">{fmtMoney(s.actualNet, { alwaysSign: true })}</td>
                              <td className="py-1.5 px-2 text-right font-mono tabular-nums">{fmtMoney(s.hypotheticalNet, { alwaysSign: true })}</td>
                              <td className={`py-1.5 px-2 text-right font-mono tabular-nums ${delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-red-400' : ''}`}>
                                {fmtMoney(delta, { alwaysSign: true })}
                              </td>
                              <td className={`py-1.5 px-2 text-right font-mono tabular-nums ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {s.skipped}/{s.actualCount}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Footer note */}
              <div className={`mt-3 text-[11px] flex items-start gap-1.5 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                <Info size={11} className="mt-0.5 shrink-0" />
                <span>
                  This is a counterfactual — same prices, same fills, but trades that wouldn't have passed the rule are removed from the curve. It doesn't account for the trades you'd have taken INSTEAD with that risk freed up. Treat the delta as evidence the rule is paying, not as a literal "you'd have made $X."
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default WhatIfBacktestModal;
